import { API_URL } from "../config";
import { useAuth } from "../store/useAuth";
import { getRefreshedToken } from "../utils/tokenRefresh";

interface HttpOptions {
  method?: string;
  token?: string;
  query?: string; // already built query string starting with ?
  body?: unknown;
  headers?: Record<string, string>;
  parse?: "json" | "text"; // default json
}

interface HttpErrorShape extends Error {
  response?: {
    status?: number;
    statusText?: string;
    data?: unknown;
  };
}

function parseBody<T>(raw: string, parse: string): T | string {
  if (parse === "json" && raw) {
    try {
      return JSON.parse(raw) as T;
    } catch {
      // keep raw text if JSON parse fails
    }
  }
  return raw as T;
}

function throwHttpError(
  res: Response,
  data: unknown,
): never {
  const parsedData =
    typeof data === "object" && data !== null
      ? (data as Record<string, unknown>)
      : {};
  const message =
    (parsedData.message as string) ||
    (parsedData.error as string) ||
    `Request failed: ${res.status} ${res.statusText}`;
  const error: HttpErrorShape = new Error(message);
  error.response = { status: res.status, statusText: res.statusText, data };
  throw error;
}

export async function http<T = unknown>(
  endpoint: string,
  options: HttpOptions = {}
): Promise<T> {
  const {
    method = "GET",
    token,
    query = "",
    body,
    headers = {},
    parse = "json",
  } = options;

  const multipart = body instanceof FormData;
  const finalHeaders: Record<string, string> = {
    ...(!multipart ? { "Content-Type": "application/json" } : {}),
    ...headers,
  };
  if (token) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  const url = API_URL + endpoint + query;
  const fetchBody = multipart
    ? body
    : body !== undefined
      ? JSON.stringify(body)
      : undefined;

  let res = await fetch(url, {
    method,
    headers: finalHeaders,
    body: fetchBody,
  });

  // On 401, try to refresh the token and retry the request once
  if (res.status === 401 && token) {
    const newToken = await getRefreshedToken();
    if (newToken) {
      finalHeaders.Authorization = `Bearer ${newToken}`;
      res = await fetch(url, {
        method,
        headers: finalHeaders,
        body: fetchBody,
      });
    } else {
      // Refresh failed — logout and throw
      useAuth.getState().logOut();
    }
  }

  // Read response body once
  const raw = await res.text();
  const data = parseBody<T>(raw, parse);

  if (!res.ok) {
    throwHttpError(res, data);
  }

  return data as T;
}
